import React, { useEffect, useState } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableSortLabel, Select, MenuItem, Checkbox, ListItemText, TablePagination } from "@mui/material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { Download, Image } from "@mui/icons-material";
import { FileExcel } from 'react-bootstrap-icons';
import ExcelJS from "exceljs";
import { format } from 'date-fns';
import html2canvas from 'html2canvas';

const TableChartCustom = ({ data, title, observaciones }) => {
  const [srcTableData, setSrcTableData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [columns, setColumns] = useState([]);
  const [columnFilters, setColumnFilters] = useState({});

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return dateString;
      }

      const rawValue = typeof dateString === 'string' ? dateString.trim() : '';
      const hasNoExplicitTime = /^\d{4}-\d{2}-\d{2}$|^\d{2}\/\d{2}\/\d{4}$/.test(rawValue);
      const hasMidnightTime = /(?:T|\s)00:00:00(?:\.0+)?(?:Z)?$/.test(rawValue);

      if (hasNoExplicitTime || hasMidnightTime) {
        return format(date, 'dd/MM/yyyy');
      }

      return format(date, 'dd/MM/yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const isValidDate = (value) => {
    const datePattern = /^\d{4}-\d{2}-\d{2}$|^\d{2}\/\d{2}\/\d{4}$|^\d{4}-\d{2}-\d{2}T/;
    return datePattern.test(value);
  };

  useEffect(() => {
    if (!data || data.length === 0) return;

    const formattedData = data.map((row) => {
      const formattedRow = { ...row };
      Object.keys(row).forEach((key) => {
        if (row[key] && isNaN(row[key]) && isValidDate(row[key])) {
          formattedRow[key] = formatDate(row[key]);
        }
      });
      return formattedRow;
    });

    setSrcTableData(formattedData);

    const keys = Object.keys(data[0]).filter(key => 
      key !== 'tipodeDato' && key !== 'titulo' && key !== 'id' && key !== 'idTipoGrafico'
    );
    setColumns(keys);
  }, [data]);

  const getUniqueColumnValues = (columnName) => {
    const values = srcTableData.map(row => row[columnName]);
    return [...new Set(values)].filter(val => val !== null && val !== undefined);
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters((prev) => ({ ...prev, [column]: value }));
    setPage(0);
  };

  const getFilteredData = () => {
    return srcTableData.filter((row) =>
      Object.entries(columnFilters).every(([key, values]) => {
        if (!values || values.length === 0) return true;
        return values.includes(row[key]?.toString());
      })
    );
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Datos");

    const headerRow = worksheet.addRow(columns);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "1976d2" }
      };
      cell.font = { color: { argb: "FFFFFF" }, bold: true };
    });

    const filteredData = getFilteredData();

    filteredData.forEach((item) => {
      const rowData = columns.map(header => item[header]);
      worksheet.addRow(rowData);
    });

    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = maxLength < 10 ? 10 : maxLength + 2;
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${title || 'tabla'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(link);
    });
  };

  const captureAndDownloadImage = async () => {
    const element = document.querySelector("#capture-table");
    if (element) {
      try {
        const canvas = await html2canvas(element, {
          backgroundColor: '#ffffff',
          scale: 2,
        });
        const image = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = image;
        link.download = `${title || 'tabla'}.png`;
        link.click();
      } catch (error) {
        console.error('Error al capturar la imagen:', error);
      }
    }
  };

  if (!data || data.length === 0) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" fontWeight="medium" mb={2}>
          {title}
        </MDTypography>
        <MDTypography variant="body2" color="text">
          No hay datos disponibles
        </MDTypography>
      </MDBox>
    );
  }

  const filteredData = getFilteredData();
  const sortedData = stableSort(filteredData, getComparator(order, orderBy));
  const paginatedData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = rowsPerPage - Math.min(rowsPerPage, filteredData.length - page * rowsPerPage);

  return (
    <MDBox p={3}>
      <MDBox style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <MDTypography variant="h5" fontWeight="medium">
          {title}
        </MDTypography>
        <MDBox style={{ display: 'flex', gap: '8px' }}>
          <MDButton
            variant="outlined"
            color="success"
            size="small"
            startIcon={<FileExcel />}
            onClick={exportToExcel}
          >
            Excel
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Image />}
            onClick={captureAndDownloadImage}
          >
            PNG
          </MDButton>
        </MDBox>
      </MDBox>

      <TableContainer id="capture-table" sx={{ height: '600px', marginBottom: 2 }} component={Paper}>
        <Table stickyHeader sx={{
          borderCollapse: 'collapse',
          '& thead th, & thead td': {
            backgroundColor: 'white',
            padding: '8px',
            borderBottom: '1px solid #ccc',
          },
          '& thead tr': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
          },
          '& thead tr:nth-of-type(2)': {
            top: 56,
            zIndex: 1,
          },
        }}>
          <TableHead sx={{ display: 'contents' }}>
            <TableRow sx={{ position: 'sticky', top: 0, backgroundColor: '#1976d2', zIndex: 2 }}>
              {columns.map((column) => (
                <TableCell key={column} sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={orderBy === column}
                    direction={orderBy === column ? order : 'asc'}
                    onClick={() => handleRequestSort(column)}
                    sx={{
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                      '&.Mui-active': { color: 'white' },
                      '&:hover': { color: 'white' },
                      color: 'white',
                    }}
                  >
                    {column}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
            <TableRow sx={{ position: 'sticky', top: 56, backgroundColor: 'white', zIndex: 1 }}>
              {columns.map((column) => (
                <TableCell key={`${column}-filter`}>
                  <Select
                    multiple
                    displayEmpty
                    fullWidth
                    value={columnFilters[column] || []}
                    onChange={(e) => handleColumnFilterChange(column, e.target.value)}
                    renderValue={(selected) => selected.length === 0 ? 'Todos' : `${selected.length} selec.`}
                    variant="standard"
                    sx={{ fontSize: '12px' }}
                  >
                    {getUniqueColumnValues(column).map((val, idx) => (
                      <MenuItem key={idx} value={val}>
                        <Checkbox checked={(columnFilters[column] || []).includes(val)} />
                        <ListItemText primary={val} />
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                {columns.map((column, colIndex) => (
                  <TableCell key={`${index}-${colIndex}`}>{row[column]}</TableCell>
                ))}
              </TableRow>
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={columns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Filas por página:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      {observaciones && (
        <MDTypography variant="caption" color="text" mt={2} style={{ fontStyle: 'italic', opacity: 0.7 }}>
          *{observaciones}
        </MDTypography>
      )}
    </MDBox>
  );
};

export default TableChartCustom;
